import {isEmpty, orderBy} from 'lodash';
import {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {accountOAuthApi} from 'api';
import {
    ActionButton,
    ConfirmButton,
    CopyText,
    Icon,
    PredefinedNotifierType
} from 'components';
import {UserContext} from 'context';
import {useApiData} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {OAuthClient, OAuthScope} from 'types';
import {dateUtils} from 'utils';
import {formatDate} from 'utils/transform';

import {OAuthAccordion} from './OAuthAccordion';
import {OAuthDetailsModal} from './OAuthDetailsModal';
import {OAuthTable} from './OAuthTable';
import {ViewSecretModal} from './ViewSecretModal';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.SETTINGS.OAUTH;

export const OAuthSettings: React.FC = () => {
    const {t} = useTranslation();

    const {settings} = useContext(UserContext);
    const {timeZone, dateFormat, timeFormat} = useMemo(() => settings.common, [settings]);

    const [selectedSecret, setSelectedSecret] = useState<string | null>(null);
    const [secretModalOpen, setSecretModalOpen] = useState(false);
    const [detailsModalProps, setDetailsModalProps] = useState<{open: boolean, data?: OAuthClient}>({open: false});

    const {send: getClients, data: clientsData = [], loading: getLoading} = useApiData(accountOAuthApi.getAll);
    const {send: getAvailableScopes, data: availableScopesData} = useApiData(accountOAuthApi.getAvailableScopes);

    const clients = useMemo(() => orderBy(clientsData, ['createdAt'], ['desc']), [clientsData]);
    const availableScopes = useMemo(() => availableScopesData?.scopes || [], [availableScopesData]);

    const onDeleteSuccess = useCallback(() => {
        getClients();
    }, [getClients]);

    const onRegenerateSuccess = useCallback((response: any) => {
        setSelectedSecret(response.clientSecret);
        setSecretModalOpen(true);
        getClients();
    }, [getClients]);

    const {send: deleteClient} = useApiData(
        accountOAuthApi.deleteOne,
        {
            onSuccess: onDeleteSuccess,
            notificationType: PredefinedNotifierType.Delete
        }
    );

    const {send: regenerateSecret, loading: regenerateLoading} = useApiData(
        accountOAuthApi.regenerateSecret,
        {
            onSuccess: onRegenerateSuccess,
            notificationType: PredefinedNotifierType.Save
        }
    );

    const handleDetailsSuccess = useCallback((response: any) => {
        if (!detailsModalProps.data) {
            setSelectedSecret(response.clientSecret);
            setSecretModalOpen(true);
        }
        getClients();
        setDetailsModalProps({open: false});
    }, [getClients, detailsModalProps.data]);

    const handleDeleteClick = useCallback((id: string) => {
        deleteClient(id);
    }, [deleteClient]);

    const handleCreateClick = useCallback(() => {
        setDetailsModalProps({open: true});
    }, []);

    const handleEditClick = useCallback((client: OAuthClient) => {
        setDetailsModalProps({open: true, data: client});
    }, []);

    const handleDetailsModalClose = useCallback(() => {
        setDetailsModalProps({open: false});
    }, []);

    const handleRegenerateClick = useCallback((id: string) => {
        regenerateSecret(id);
    }, [regenerateSecret]);

    const formatScopes = useCallback((scopes: OAuthScope[]) => (
        <div>
            {scopes.map((scope, index) => (
                <div key={index} className='tw-text-sm'>
                    {scope}
                </div>
            ))}
        </div>
    ), []);

    const filterAvailableScopes = useCallback((scopes: OAuthScope[]) =>
        scopes.filter((scope) => availableScopes.includes(scope)),
    [availableScopes]);

    const renderClientId = useCallback((clientId: string, labelPlacement?: 'left' | 'right') => (
        <CopyText
            text={clientId}
            iconSize='sm'
            textInTooltip={false}
            labelPlacement={labelPlacement}
            className='tw-text-sm tw-break-all'
        />
    ), []);

    const renderSecret = useCallback((client: OAuthClient) => (
        <span className='tw-ts-code tw-text-sm'>
            {client.clientSecretMask}
            {'*'.repeat(12)}
        </span>
    ), []);

    const renderScopes = useCallback((client: OAuthClient, labelPlacement?: 'left' | 'right') => {
        const filtered = filterAvailableScopes(client.scopes);
        if (isEmpty(filtered)) {
            return null;
        }
        return (
            <CopyText
                text={filtered.join(' ')}
                label={formatScopes(filtered)}
                copiedLabelOnly
                textInTooltip={false}
                iconSize='sm'
                labelPlacement={labelPlacement}
                className='tw-text-sm tw-break-all'
            />
        );
    }, [formatScopes, filterAvailableScopes]);

    const renderCreated = useCallback((createdAt: Date) => (
        formatDate(createdAt, timeZone, dateUtils.getDateTimeFormat(dateFormat, timeFormat))
    ), [timeZone, dateFormat, timeFormat]);

    const renderUpdated = useCallback((updatedAt: Date) => (
        formatDate(updatedAt, timeZone, dateUtils.getDateTimeFormat(dateFormat, timeFormat))
    ), [timeZone, dateFormat, timeFormat]);

    const renderActions = useCallback((client: OAuthClient) => (
        <div className='tw-flex tw-justify-end'>
            <ConfirmButton
                iconProps={{
                    name: 'refresh',
                    color: 'primary',
                    button: true,
                    group: true,
                    stopPropagation: true,
                    title: t(T_KEYS.BUTTONS.REGENERATE)
                }}
                disabled={regenerateLoading}
                modal={{
                    description: t(T_KEYS.REGENERATE_MODAL.DESCRIPTION)
                }}
                onConfirm={() => handleRegenerateClick(client.id)}
            />
            <Icon
                name='edit'
                color='primary'
                button
                group
                title={t(T_KEYS.BUTTONS.EDIT)}
                stopPropagation
                onClick={() => handleEditClick(client)}
            />
            <ConfirmButton
                iconProps={{
                    name: 'delete',
                    color: 'alert',
                    button: true,
                    group: true,
                    stopPropagation: true,
                    title: t(T_KEYS.BUTTONS.DELETE)
                }}
                modal={{
                    description: t(T_KEYS.DELETE_MODAL.DESCRIPTION)
                }}
                onConfirm={() => handleDeleteClick(client.id)}
            />
        </div>
    ), [regenerateLoading, handleRegenerateClick, handleEditClick, handleDeleteClick, t]);

    useEffect(() => {
        getClients();
        getAvailableScopes();
    }, [getClients, getAvailableScopes]);

    return (
        <div className='tw-relative'>
            <div className='tw-flex tw-justify-end tw-mb-6'>
                <ActionButton
                    action='create'
                    onClick={handleCreateClick}
                >
                    {t(T_KEYS.BUTTONS.CREATE_CLIENT)}
                </ActionButton>
            </div>
            <div className='-lg:tw-hidden'>
                <OAuthTable
                    loading={getLoading}
                    clients={clients}
                    renderClientId={renderClientId}
                    renderSecret={renderSecret}
                    renderScopes={renderScopes}
                    renderCreated={renderCreated}
                    renderUpdated={renderUpdated}
                    renderActions={renderActions}
                />
            </div>
            <div className='lg:tw-hidden'>
                <OAuthAccordion
                    loading={getLoading}
                    clients={clients}
                    renderClientId={renderClientId}
                    renderSecret={renderSecret}
                    renderScopes={renderScopes}
                    renderCreated={renderCreated}
                    renderUpdated={renderUpdated}
                    renderActions={renderActions}
                />
            </div>

            <OAuthDetailsModal
                isOpen={detailsModalProps.open}
                data={detailsModalProps.data}
                availableScopes={availableScopes}
                onClose={handleDetailsModalClose}
                onSuccess={handleDetailsSuccess}
            />

            <ViewSecretModal
                isOpen={secretModalOpen}
                secret={selectedSecret}
                onClose={() => {
                    setSecretModalOpen(false);
                    setSelectedSecret(null);
                }}
            />
        </div>
    );
};